/**
 * The only source of randomness in the pure core (docs/spec/06 R2): xoshiro128** seeded through
 * splitmix32. Integer arithmetic only, so it gives the same numbers on every engine.
 */
export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number) {
    let s = seed >>> 0;
    const next = (): number => {
      s = (s + 0x9e3779b9) >>> 0;
      let z = s;
      z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
      z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
      return (z ^ (z >>> 16)) >>> 0;
    };
    this.a = next();
    this.b = next();
    this.c = next();
    this.d = next();
    if ((this.a | this.b | this.c | this.d) === 0) this.a = 1;
  }

  /** A uniform unsigned 32-bit integer. */
  next(): number {
    const result = Math.imul(rotl(Math.imul(this.b, 5), 7), 9) >>> 0;
    const t = this.b << 9;
    this.c ^= this.a;
    this.d ^= this.b;
    this.b ^= this.c;
    this.a ^= this.d;
    this.c ^= t;
    this.d = rotl(this.d, 11);
    return result;
  }

  /** A uniform integer in [0, n). */
  int(n: number): number {
    return Math.floor((this.next() / 4294967296) * n);
  }

  /** True one time in `n`. */
  oneIn(n: number): boolean {
    return this.int(n) === 0;
  }

  /** Become an exact copy of `other` (the bot's look-ahead runs a private copy of the cave). */
  copyFrom(other: Rng): void {
    [this.a, this.b, this.c, this.d] = other.save();
  }

  /** The full state, for replays and tests. */
  save(): [number, number, number, number] {
    return [this.a, this.b, this.c, this.d];
  }
}

function rotl(x: number, k: number): number {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

/** Mixes numbers into one 32-bit seed (cave id, level, attempt ...). */
export function mixSeed(...parts: readonly number[]): number {
  let h = 0x811c9dc5;
  for (const p of parts) {
    h = Math.imul(h ^ (p >>> 0), 0x01000193) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  }
  return h >>> 0;
}

/** A seed from text (a cave name, a date). */
export function seedFromText(text: string): number {
  const codes: number[] = [];
  for (let i = 0; i < text.length; i++) codes.push(text.charCodeAt(i));
  return mixSeed(...codes);
}
